import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { ENABLE_USER_BILLING } from '../config/featureFlags'
import { api } from '../lib/api'
import {
  BILLING_NUDGE_TZ,
  BILLING_PAYWALL_MODAL_LAST_DATE_KEY,
  STORE_SUBSCRIPTION_OVERLAP_MODAL_LAST_DATE_KEY,
  TRIAL_SUBSCRIPTION_NOTICE_DISMISSED_KEY_PREFIX,
  billingPaywallNudgeNeedsUi,
  computeBillingPaywallNudge,
  computeStoreSubscriptionOverlapNotice,
  computeTrialSubscriptionNotice,
  dateStringInTimeZone,
} from '../lib/billingPaywallNudge'

function todayKstYmd() {
  return dateStringInTimeZone(new Date(), BILLING_NUDGE_TZ)
}

function readModalLastDate(key = BILLING_PAYWALL_MODAL_LAST_DATE_KEY) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeModalLastDate(ymd, key = BILLING_PAYWALL_MODAL_LAST_DATE_KEY) {
  try {
    localStorage.setItem(key, ymd)
  } catch {
    /* ignore */
  }
}

function readDismissedFlag(key) {
  if (!key) return false
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function writeDismissedFlag(key) {
  if (!key) return
  try {
    localStorage.setItem(key, '1')
  } catch {
    /* ignore */
  }
}

function trialNoticeDismissKey(user, notice) {
  if (!notice?.visible) return null
  const userPart = user?.id ?? user?.email ?? 'anonymous'
  const subscriptionPart = notice.subscriptionId || notice.endsAtYmd || 'current'
  const eventPart = notice.level === 'start'
    ? 'start'
    : `${notice.level}:${notice.todayYmd || ''}`
  return `${TRIAL_SUBSCRIPTION_NOTICE_DISMISSED_KEY_PREFIX}:${userPart}:${subscriptionPart}:${eventPart}`
}

/** @param {import('../lib/billingPaywallNudge').TrialSubscriptionNotice} notice */
function trialNoticeTitle(notice) {
  return notice.level === 'start' ? '무료체험이 시작되었습니다' : '무료체험 기간 안내'
}

/** @param {import('../lib/billingPaywallNudge').TrialSubscriptionNotice} notice */
function trialNoticeMessage(notice, billingUiEnabled) {
  const end = notice.endsAtYmd ? `\n무료체험 종료 예정일은 ${notice.endsAtYmd}(KST)입니다.` : ''
  const action = billingUiEnabled
    ? '무료체험은 별도 결제 동의 없이 유료로 자동 전환되지 않습니다. 계속 이용하시려면 체험 종료 전 구독을 진행해 주세요.'
    : '무료체험은 별도 결제 동의 없이 유료로 자동 전환되지 않습니다. 계속 이용하시려면 내 정보에서 계정 상태를 확인하거나 고객문의로 요청해 주세요.'
  if (notice.level === 'start') {
    return `${notice.trialDays || 7}일 무료체험이 부여되었습니다. 지금부터 주요 기능을 이용해 볼 수 있습니다.\n${action}${end}`
  }
  if (notice.level === 'lastDay') {
    return `무료체험 기간이 오늘 종료됩니다. 구독시작으로 업무를 이어가세요! ${action}${end}`
  }
  return `무료체험 기간이 ${notice.daysLeft}일 남았습니다. ${action}${end}`
}

/** @param {import('../lib/billingPaywallNudge').BillingPaywallNudgeLevel} level */
function bannerTone(level) {
  if (level === 'warn7' || level === 'warn4') return 'bg-amber-50 border-amber-200 text-amber-950'
  return 'bg-red-50 border-red-200 text-red-950'
}

/** @param {import('../lib/billingPaywallNudge').BillingPaywallNudge} nudge */
function bannerMessage(nudge) {
  const end = nudge.endsAtYmd ? nudge.endsAtYmd.replace(/-/g, '.') : ''
  switch (nudge.level) {
    case 'suspended':
      return '구독이 정지되어 있습니다. 업무 기능을 계속 이용하려면 구독을 복구·결제해 주세요.'
    case 'expired':
      return '구독이 만료되었습니다. 서비스를 이용하려면 결제로 구독을 갱신해 주세요.'
    case 'lastDay':
      return end
        ? `구독 만료일이 오늘(${end}, KST 기준)입니다. 이용이 중단되지 않도록 결제를 진행해 주세요.`
        : '구독 만료일이 오늘(KST 기준)입니다. 이용이 중단되지 않도록 결제를 진행해 주세요.'
    case 'warn1':
      return end
        ? `구독이 ${end}(KST)에 만료됩니다. 하루 전 안내입니다. 결제를 진행해 주세요.`
        : '구독 만료 하루 전입니다. 결제를 진행해 주세요.'
    case 'warn4':
      return end
        ? `구독이 ${end}(KST)에 만료됩니다. 4일 전 안내입니다. 결제를 진행해 주세요.`
        : '구독 만료 4일 전입니다. 결제를 진행해 주세요.'
    case 'warn7':
      return end
        ? `구독이 ${end}(KST)에 만료됩니다. 7일 전 안내입니다. 결제를 진행해 주세요.`
        : '구독 만료 7일 전입니다. 결제를 진행해 주세요.'
    default:
      return ''
  }
}

/** @param {import('../lib/billingPaywallNudge').BillingPaywallNudge} nudge */
function modalMessage(nudge, billingUiEnabled) {
  const base = bannerMessage(nudge)
  if (billingUiEnabled) {
    return `${base} 아래 버튼에서 구독·결제 화면으로 이동할 수 있습니다.`
  }
  return `${base} 사용자 결제 화면이 비공개인 빌드에서는 내 정보로 이동해 계정을 확인하거나 관리자에게 문의해 주세요.`
}

function overlapBannerMessage(notice) {
  const capped = notice.creditCapped
    ? ` 내부 크레딧은 최대 ${notice.maxCreditDays}일까지 적립되어 현재 상한에 도달했습니다.`
    : ` 내부 크레딧은 최대 ${notice.maxCreditDays}일까지 적립됩니다.`
  return `조직계약이 적용 중이지만 App Store/Google Play 개인 구독도 유지되고 있습니다.${capped}`
}

function overlapModalMessage(notice) {
  const days = notice.remainingCreditDays > 0
    ? `현재 적립된 개인 구독 크레딧은 ${notice.remainingCreditDays}일입니다.`
    : '현재 적립된 개인 구독 크레딧은 없습니다.'
  const end = notice.subscriptionEndsAtYmd
    ? ` 스토어 구독 만료 예정일은 ${notice.subscriptionEndsAtYmd}입니다.`
    : ''
  return `${days}${end} 조직계약 혜택을 이용하는 동안 스토어 정기구독이 계속 결제될 수 있으니, 스토어 구독 관리 화면에서 유지 여부를 확인해 주세요.`
}

/**
 * 로그인 셸: 구독 만료 임박·만료·정지 시 상단 배너(항상) + 모달(한국 달력일 기준 1일 1회).
 * 운영자·TRIAL·조직 전액(ORG_PAYS) 유효 계약은 제외. 서버 권한/쿼터 검증은 별도.
 */
export default function BillingPaywallShellNudge() {
  const { user } = useAuth()
  const roles = user?.roles || []
  const isOperator = roles.includes(Roles.OPERATOR)

  /** 구독 데이터는 BE에 항상 있으므로 플래그와 무관히 조회한다. (`/billing` UI만 플래그로 숨김) */
  const fetchEnabled = Boolean(user && !isOperator)
  const billingCtaPath = ENABLE_USER_BILLING ? '/billing/subscribe' : '/profile'
  const billingCtaBannerLabel = ENABLE_USER_BILLING ? '구독하기' : '내 정보로 이동'
  const billingCtaModalLabel = ENABLE_USER_BILLING ? '구독하기' : '내 정보 보기'

  const billingQuery = useQuery({
    queryKey: ['billing-me-paywall-nudge', user?.id],
    queryFn: async () => {
      const res = await api.get('/billing/me')
      return res?.data?.data ?? null
    },
    enabled: fetchEnabled,
    staleTime: 60_000,
    retry: false,
  })

  const nudge = React.useMemo(() => {
    if (!fetchEnabled || billingQuery.isError || !billingQuery.data) {
      return computeBillingPaywallNudge(null)
    }
    return computeBillingPaywallNudge(billingQuery.data)
  }, [fetchEnabled, billingQuery.isError, billingQuery.data])

  const overlapNotice = React.useMemo(() => {
    if (!fetchEnabled || billingQuery.isError || !billingQuery.data) {
      return computeStoreSubscriptionOverlapNotice(null)
    }
    return computeStoreSubscriptionOverlapNotice(billingQuery.data)
  }, [fetchEnabled, billingQuery.isError, billingQuery.data])

  const trialNotice = React.useMemo(() => {
    if (!fetchEnabled || billingQuery.isError || !billingQuery.data) {
      return computeTrialSubscriptionNotice(null)
    }
    return computeTrialSubscriptionNotice(billingQuery.data)
  }, [fetchEnabled, billingQuery.isError, billingQuery.data])

  const showBanner = fetchEnabled && billingPaywallNudgeNeedsUi(nudge.level)
  const showOverlap = fetchEnabled && overlapNotice.visible
  const showTrialNotice = fetchEnabled && trialNotice.visible
  const [modalOpen, setModalOpen] = React.useState(false)
  const [overlapModalOpen, setOverlapModalOpen] = React.useState(false)
  const [trialModalOpen, setTrialModalOpen] = React.useState(false)
  const trialDismissKey = React.useMemo(
    () => trialNoticeDismissKey(user, trialNotice),
    [user, trialNotice],
  )

  React.useEffect(() => {
    if (!showBanner) {
      setModalOpen(false)
      return
    }
    const today = todayKstYmd()
    if (!today) return
    const last = readModalLastDate()
    if (last === today) {
      setModalOpen(false)
      return
    }
    setModalOpen(true)
  }, [showBanner, nudge.level, nudge.endsAtYmd])

  const dismissModal = React.useCallback(() => {
    const today = todayKstYmd()
    if (today) writeModalLastDate(today)
    setModalOpen(false)
  }, [])

  React.useEffect(() => {
    if (!showOverlap) {
      setOverlapModalOpen(false)
      return
    }
    const today = todayKstYmd()
    if (!today) return
    const last = readModalLastDate(STORE_SUBSCRIPTION_OVERLAP_MODAL_LAST_DATE_KEY)
    if (last === today) {
      setOverlapModalOpen(false)
      return
    }
    setOverlapModalOpen(true)
  }, [showOverlap, overlapNotice.remainingCreditDays, overlapNotice.creditCapped])

  const dismissOverlapModal = React.useCallback(() => {
    const today = todayKstYmd()
    if (today) writeModalLastDate(today, STORE_SUBSCRIPTION_OVERLAP_MODAL_LAST_DATE_KEY)
    setOverlapModalOpen(false)
  }, [])

  React.useEffect(() => {
    if (!showTrialNotice || !trialDismissKey) {
      setTrialModalOpen(false)
      return
    }
    if (readDismissedFlag(trialDismissKey)) {
      setTrialModalOpen(false)
      return
    }
    setTrialModalOpen(true)
  }, [showTrialNotice, trialDismissKey])

  const dismissTrialModal = React.useCallback(() => {
    writeDismissedFlag(trialDismissKey)
    setTrialModalOpen(false)
  }, [trialDismissKey])
  if (!showBanner && !showOverlap && !showTrialNotice) return null

  return (
    <>
      {showBanner && (
        <div className={`border-t px-4 py-2.5 text-center text-sm leading-snug ${bannerTone(nudge.level)}`}>
          <span>{bannerMessage(nudge)}</span>
          {' '}
          <Link className="font-semibold underline underline-offset-2 hover:opacity-90" to={billingCtaPath}>
            {billingCtaBannerLabel}
          </Link>
        </div>
      )}

      {showOverlap && (
        <div className="border-t border-sky-200 bg-sky-50 px-4 py-2.5 text-center text-sm leading-snug text-sky-950">
          <span>{overlapBannerMessage(overlapNotice)}</span>
          {' '}
          <Link className="font-semibold underline underline-offset-2 hover:opacity-90" to="/billing">
            구독 확인
          </Link>
        </div>
      )}

      {modalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sgez-billing-paywall-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) dismissModal()
          }}
        >
          <div className="w-full max-w-md rounded-lg border bg-white p-5 shadow-xl">
            <h2 id="sgez-billing-paywall-title" className="text-base font-semibold text-slate-900">
              구독·결제 안내
            </h2>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
              {modalMessage(nudge, ENABLE_USER_BILLING)}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                onClick={dismissModal}
              >
                닫기
              </button>
              <Link
                to={billingCtaPath}
                className="rounded bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                onClick={dismissModal}
              >
                {billingCtaModalLabel}
              </Link>
            </div>
          </div>
        </div>
      )}


      {trialModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sgez-trial-subscription-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) dismissTrialModal()
          }}
        >
          <div className="w-full max-w-md rounded-lg border bg-white p-5 shadow-xl">
            <h2 id="sgez-trial-subscription-title" className="text-base font-semibold text-slate-900">
              {trialNoticeTitle(trialNotice)}
            </h2>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
              {trialNoticeMessage(trialNotice, ENABLE_USER_BILLING)}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                onClick={dismissTrialModal}
              >
                닫기
              </button>
              <Link
                to="/billing/subscribe"
                className="rounded bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                onClick={dismissTrialModal}
              >
                구독하러가기
              </Link>
            </div>
          </div>
        </div>
      )}

      {overlapModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sgez-store-overlap-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) dismissOverlapModal()
          }}
        >
          <div className="w-full max-w-md rounded-lg border bg-white p-5 shadow-xl">
            <h2 id="sgez-store-overlap-title" className="text-base font-semibold text-slate-900">
              스토어 구독 확인
            </h2>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
              {overlapModalMessage(overlapNotice)}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                onClick={dismissOverlapModal}
              >
                닫기
              </button>
              <Link
                to="/billing"
                className="rounded bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                onClick={dismissOverlapModal}
              >
                구독 확인
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
